import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';

@Component({
    selector: 'app-ui-conflict-details',
    templateUrl: './conflict-details.component.html',
    styleUrls: ['./conflict-details.component.scss'],
    standalone: true,
    imports: [CommonModule],
})
export class ConflictDetailsComponent {
    @Input() data: any = {};
    @Input() variant: 'identity' | 'service-policy' = 'identity';
    readonly conflictDescription = 'A conflict in a Ziti network occurs when the same identity can both access (dial) and host (bind) services that share overlapping addresses such as the same IP, hostname, or port. In this situation, traffic may loop back to its source instead of reaching the intended destination. These loopbacks are often subtle, hard to detect, and can lead to unstable behavior or even continuous traffic loops in the network.';

    get conflicts() {
        return Array.isArray(this.data?.conflictDetails?.conflicts) ? this.data.conflictDetails.conflicts : [];
    }

    get identities() {
        return Array.isArray(this.data?.conflictDetails?.identities) ? this.data.conflictDetails.identities : [];
    }

    get identityName() {
        return this.data?.conflictDetails?.name || this.data?.name || 'Unknown identity';
    }

    get selectedRoleAttributes() {
        const selectedRoles = this.getRoleList(this.data?.roleAttributes || []);
        if (selectedRoles.length > 0) {
            return selectedRoles;
        }

        return this.getRoleList(this.data?.conflictDetails?.roleAttributes || []);
    }

    get policyIdentityAttributes() {
        return this.getRoleList(
            this.data?.conflictDetails?.identityAttributes
            || this.data?.conflictDetails?.identityAttribute
            || this.data?.identityRoles
            || []
        );
    }

    get policyServiceAttributes() {
        return this.getRoleList(
            this.data?.conflictDetails?.serviceAttributes
            || this.data?.conflictDetails?.serviceAttribute
            || this.data?.serviceRoles
            || []
        );
    }

    get totalAffectedIdentityCount() {
        const pagedIdentityCount = Number(this.data?.pagination?.totalElements ?? this.data?.conflictDetails?.page?.totalElements);
        if (Number.isFinite(pagedIdentityCount) && pagedIdentityCount > 0) {
            return pagedIdentityCount;
        }

        return this.identities.length;
    }

    get showLoadedIdentityCount() {
        return this.variant === 'service-policy' && this.totalAffectedIdentityCount > this.identities.length;
    }

    getAddressTargets(address: any) {
        const ips = Array.isArray(address?.ips) ? address.ips : [];
        const hosts = Array.isArray(address?.hosts) ? address.hosts : [];
        return [...ips, ...hosts];
    }

    getAddressHosts(address: any) {
        return Array.isArray(address?.hosts) ? address.hosts.filter((host: any) => !!host) : [];
    }

    getAddressIps(address: any) {
        return Array.isArray(address?.ips) ? address.ips.filter((ip: any) => !!ip) : [];
    }

    getAddressConfigType(address: any) {
        return address?.configType || address?.type || address?._links?.configType?.title || 'Unknown';
    }

    getAddressPorts(address: any) {
        const ports = Array.isArray(address?.ports) ? address.ports : [];
        if (!ports.length) {
            return 'None';
        }

        return ports
            .map((port: any) => {
                if (port?.from === undefined || port?.from === null) {
                    return 'Unknown';
                }
                return port.from === port.to ? `${port.from}` : `${port.from}-${port.to}`;
            })
            .join(', ');
    }

    getRoleSummary(roles: any[] = []) {
        return roles.length > 0 ? roles.join(', ') : 'None';
    }

    getRoleList(roles: any[] = []) {
        return Array.isArray(roles) ? roles.filter((role) => !!role).map((role) => this.formatRoleAttribute(role)) : [];
    }

    getIdentityConflictReason(conflict: any) {
        const bindServiceName = conflict?.bindService?.name || 'the bind service';
        const dialServiceName = conflict?.dialService?.name || 'the dial service';
        const bindPolicyName = conflict?.bindServicePolicy?.name || 'the bind policy';
        const dialPolicyName = conflict?.dialServicePolicy?.name || 'the dial policy';
        const overlapEvidence = this.getAddressOverlapEvidence(conflict?.ingress, conflict?.egress);

        return `This identity matches ${bindPolicyName} and ${dialPolicyName}. `
            + `That means it could host ${bindServiceName} and also dial ${dialServiceName}. `
            + `${overlapEvidence}`;
    }

    getIdentityConflictSignals(conflict: any) {
        const bindPolicyRoles = this.getRoleList(conflict?.bindServicePolicy?.identityRoles);
        const dialPolicyRoles = this.getRoleList(conflict?.dialServicePolicy?.identityRoles);

        return [
            `This identity's current attributes: ${this.getRoleSummary(this.selectedRoleAttributes)}`,
            this.getPolicySelectorMessage(conflict?.bindServicePolicy?.name || 'Bind policy', conflict?.bindServicePolicy?.semantic, bindPolicyRoles),
            this.getPolicySelectorMessage(conflict?.dialServicePolicy?.name || 'Dial policy', conflict?.dialServicePolicy?.semantic, dialPolicyRoles),
            `Overlap found by the API: ${this.getAddressOverlapShortEvidence(conflict?.ingress, conflict?.egress)}`,
        ];
    }

    getPolicyReason(identityEntry: any, conflict: any) {
        const identityName = identityEntry?.identity?.name || 'This identity';
        const bindPolicyName = conflict?.bindServicePolicy?.name || 'the bind policy';
        const dialPolicyName = conflict?.dialServicePolicy?.name || 'the dial policy';
        const bindServiceName = conflict?.bindService?.name || 'the bind service';
        const dialServiceName = conflict?.dialService?.name || 'the dial service';
        const overlapEvidence = this.getAddressOverlapEvidence(conflict?.ingress, conflict?.egress);

        return `${identityName} matches this policy and also matches ${bindPolicyName} and ${dialPolicyName}. `
            + `That means the same identity could host ${bindServiceName} and also dial ${dialServiceName}. `
            + `${overlapEvidence}`;
    }

    getPolicySignals(identityEntry: any, conflict: any) {
        const currentIdentityRoles = this.getRoleList(
            this.data?.conflictDetails?.identityAttributes
            || this.data?.conflictDetails?.identityAttribute
            || this.data?.identityRoles
            || []
        );
        const bindPolicyRoles = this.getRoleList(conflict?.bindServicePolicy?.identityRoles);
        const dialPolicyRoles = this.getRoleList(conflict?.dialServicePolicy?.identityRoles);

        return [
            `Matching identity roles: ${this.getRoleSummary(this.getRoleList(identityEntry?.identity?.roleAttributes || []))}`,
            this.getPolicySelectorMessage('Current policy', this.data?.conflictDetails?.semantic, currentIdentityRoles),
            this.getPolicySelectorMessage(conflict?.bindServicePolicy?.name || 'Bind policy', conflict?.bindServicePolicy?.semantic, bindPolicyRoles),
            this.getPolicySelectorMessage(conflict?.dialServicePolicy?.name || 'Dial policy', conflict?.dialServicePolicy?.semantic, dialPolicyRoles),
            `Address overlap evidence: ${this.getAddressOverlapShortEvidence(conflict?.ingress, conflict?.egress)}`,
        ];
    }

    getOverlapMessage(overlap: any) {
        const evidence: string[] = [];

        if (this.getOverlapHosts(overlap).length > 0) {
            evidence.push('host values');
        }

        if (this.getOverlapIps(overlap).length > 0) {
            evidence.push('IP ranges');
        }

        if (this.getOverlapPorts(overlap).length > 0) {
            evidence.push('port ranges');
        }

        if (!evidence.length) {
            return 'Found overlapping address details that may create a potential conflict.';
        }

        return `This potential conflict may be happening because both sides overlap on the same ${evidence.join(', ')}.`;
    }

    hasOverlapDetails(overlap: any) {
        return this.getOverlapHosts(overlap).length > 0
            || this.getOverlapIps(overlap).length > 0
            || this.getOverlapPorts(overlap).length > 0;
    }

    getOverlapHosts(overlap: any) {
        return Array.isArray(overlap?.hosts) ? overlap.hosts.filter((host: any) => !!host) : [];
    }

    getOverlapIps(overlap: any) {
        return Array.isArray(overlap?.ips) ? overlap.ips.filter((ip: any) => !!ip) : [];
    }

    getOverlapPorts(overlap: any) {
        const ports = Array.isArray(overlap?.ports) ? overlap.ports : [];
        return ports
            .map((port: any) => {
                if (port?.from === undefined || port?.from === null) {
                    return '';
                }
                return port.from === port.to ? `${port.from}` : `${port.from}-${port.to}`;
            })
            .filter((port: string) => !!port);
    }

    getIdentityEntryRoleAttributes(identityEntry: any) {
        return this.getRoleList(identityEntry?.identity?.roleAttributes || []);
    }

    private getAddressSummary(address: any) {
        const targets = this.getAddressTargets(address);
        const targetSummary = targets.length > 0 ? targets.join(', ') : 'no targets';
        return `${targetSummary} on ports ${this.getAddressPorts(address)}`;
    }

    private getPolicySelectorMessage(policyName: string, semantic: string, roles: string[]) {
        const selectorSummary = this.getRoleSummary(roles);
        if (!roles.length) {
            return `${policyName} has no explicit identity selector returned by the API`;
        }

        return `${policyName} matches identities when ${this.getSemanticLabel(semantic)} for ${selectorSummary}`;
    }

    private getSemanticLabel(semantic: string) {
        return semantic === 'AllOf' ? 'all listed roles required' : 'any listed role allowed';
    }

    private getAddressOverlapEvidence(ingress: any, egress: any) {
        return `The API also shows address overlap between the bind egress side and the dial ingress side: `
            + `${this.getAddressOverlapShortEvidence(ingress, egress)}. `
            + `Ingress address: ${this.getAddressSummary(ingress)}. Egress address: ${this.getAddressSummary(egress)}.`;
    }

    private getAddressOverlapShortEvidence(ingress: any, egress: any) {
        const sharedHosts = this.getCommonValues(this.getAddressHosts(ingress), this.getAddressHosts(egress));
        const sharedIps = this.getCommonValues(this.getAddressIps(ingress), this.getAddressIps(egress));
        const sharedPorts = this.getSharedPorts(ingress, egress);
        const evidence: string[] = [];

        if (sharedHosts.length > 0) {
            evidence.push(`shared hosts ${sharedHosts.join(', ')}`);
        }

        if (sharedIps.length > 0) {
            evidence.push(`shared IPs ${sharedIps.join(', ')}`);
        }

        if (sharedPorts.length > 0) {
            evidence.push(`overlapping ports ${sharedPorts.join(', ')}`);
        }

        if (this.getAddressConfigType(ingress) === this.getAddressConfigType(egress)) {
            evidence.push(`matching config type ${this.getAddressConfigType(ingress)}`);
        }

        return evidence.length > 0 ? evidence.join('; ') : 'an overlapping ingress and egress path';
    }

    private getCommonValues(left: any[] = [], right: any[] = []) {
        const rightSet = new Set(right.map((value) => `${value}`));
        return left.filter((value) => rightSet.has(`${value}`));
    }

    private getSharedPorts(leftAddress: any, rightAddress: any) {
        const leftPorts = Array.isArray(leftAddress?.ports) ? leftAddress.ports : [];
        const rightPorts = Array.isArray(rightAddress?.ports) ? rightAddress.ports : [];
        const overlaps = new Set<string>();

        leftPorts.forEach((leftPort: any) => {
            rightPorts.forEach((rightPort: any) => {
                const leftFrom = Number(leftPort?.from);
                const leftTo = Number(leftPort?.to ?? leftPort?.from);
                const rightFrom = Number(rightPort?.from);
                const rightTo = Number(rightPort?.to ?? rightPort?.from);

                if ([leftFrom, leftTo, rightFrom, rightTo].some((value) => Number.isNaN(value))) {
                    return;
                }

                const overlapFrom = Math.max(leftFrom, rightFrom);
                const overlapTo = Math.min(leftTo, rightTo);
                if (overlapFrom <= overlapTo) {
                    overlaps.add(overlapFrom === overlapTo ? `${overlapFrom}` : `${overlapFrom}-${overlapTo}`);
                }
            });
        });

        return Array.from(overlaps);
    }

    private formatRoleAttribute(role: any) {
        const value = `${role || ''}`.trim();
        if (!value) {
            return '';
        }

        return value.startsWith('#') || value.startsWith('@') ? value : `#${value}`;
    }
}
